'use client'

import React, { useMemo, useRef, useState } from 'react'

interface TagInputProps {
  label: string
  value: string[]
  onChange: (tags: string[]) => void
  suggestions?: string[]
  placeholder?: string
  disabled?: boolean
  required?: boolean
  maxTags?: number
}

/**
 * TagInput — chip-based multi-value input with autocomplete.
 *
 * - Enter / comma / Tab adds current text as tag
 * - Backspace on empty input removes last tag
 * - Case-insensitive de-duplication
 * - Suggestions shown in a dropdown; already-selected tags are filtered out
 */
export function TagInput({
  label,
  value,
  onChange,
  suggestions = [],
  placeholder,
  disabled = false,
  required = false,
  maxTags
}: TagInputProps) {
  const [input, setInput] = useState('')
  const [focused, setFocused] = useState(false)
  const [highlight, setHighlight] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  const normalized = useMemo(
    () => new Set(value.map((t) => t.trim().toLowerCase())),
    [value]
  )

  const filteredSuggestions = useMemo(() => {
    const q = input.trim().toLowerCase()
    return suggestions
      .filter((s) => s && !normalized.has(s.trim().toLowerCase()))
      .filter((s) => (q ? s.toLowerCase().includes(q) : true))
      .slice(0, 8)
  }, [suggestions, input, normalized])

  const addTag = (raw: string) => {
    const tag = raw.trim()
    if (!tag) return
    if (maxTags && value.length >= maxTags) return
    if (normalized.has(tag.toLowerCase())) {
      setInput('')
      return
    }
    onChange([...value, tag])
    setInput('')
    setHighlight(0)
  }

  const removeTag = (idx: number) => {
    const next = value.slice()
    next.splice(idx, 1)
    onChange(next)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
      if (input.trim()) {
        e.preventDefault()
        if (filteredSuggestions[highlight] && e.key !== ',') {
          addTag(filteredSuggestions[highlight])
        } else {
          addTag(input)
        }
      }
    } else if (e.key === 'Backspace' && !input && value.length > 0) {
      removeTag(value.length - 1)
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlight((h) => Math.min(h + 1, filteredSuggestions.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight((h) => Math.max(h - 1, 0))
    } else if (e.key === 'Escape') {
      setFocused(false)
      inputRef.current?.blur()
    }
  }

  const isActive = focused || value.length > 0 || input.length > 0
  const showDropdown = focused && filteredSuggestions.length > 0

  return (
    <div className="fi-wrapper" style={{ position: 'relative' }}>
      <div
        className="fi-inner"
        onClick={() => inputRef.current?.focus()}
        style={{ cursor: 'text' }}
      >
        <div
          className="fi-input"
          data-active={isActive || undefined}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 6,
            minHeight: 44,
            paddingTop: value.length > 0 || isActive ? 20 : 12,
            paddingBottom: 8,
            paddingLeft: 12,
            paddingRight: 12,
            height: 'auto'
          }}
        >
          {value.map((tag, idx) => (
            <span
              key={`${tag}-${idx}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '2px 6px 2px 10px',
                background: '#eff6ff',
                color: '#1d4ed8',
                border: '1px solid #bfdbfe',
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 500,
                lineHeight: '18px'
              }}
            >
              {tag}
              {!disabled && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    removeTag(idx)
                  }}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    color: '#1d4ed8',
                    cursor: 'pointer',
                    padding: 0,
                    width: 16,
                    height: 16,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                    lineHeight: 1,
                    borderRadius: '50%'
                  }}
                  aria-label={`${tag} etiketini kaldır`}
                >
                  ×
                </button>
              )}
            </span>
          ))}
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => {
              setInput(e.target.value)
              setHighlight(0)
            }}
            onFocus={() => setFocused(true)}
            onBlur={() => setTimeout(() => setFocused(false), 150)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            placeholder={value.length === 0 ? placeholder || '' : ''}
            style={{
              flex: 1,
              minWidth: 80,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontSize: 14,
              color: '#111827',
              padding: 0
            }}
          />
        </div>
        <label
          className={`fi-label${isActive ? ' fi-label--float' : ''}`}
          data-disabled={disabled || undefined}
        >
          {label} {required && <span className="fi-required">*</span>}
        </label>
      </div>

      {showDropdown && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
            maxHeight: 220,
            overflowY: 'auto',
            zIndex: 50
          }}
        >
          {filteredSuggestions.map((s, idx) => (
            <button
              key={s}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault()
                addTag(s)
              }}
              onMouseEnter={() => setHighlight(idx)}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '8px 12px',
                border: 'none',
                background: highlight === idx ? '#f3f4f6' : 'transparent',
                color: '#111827',
                fontSize: 13,
                cursor: 'pointer'
              }}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
